/**
 * useReports Hook
 *
 * Provides report data by querying real Firestore collections
 * and aggregating data by date ranges.
 */

import { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export interface DateRange {
  start: Date;
  end: Date;
}

export type ReportType = 'users' | 'bookings' | 'revenue' | 'payouts';

export interface ReportDataPoint {
  date: string;
  value: number;
  label: string;
}

export interface ReportSummary {
  total: number;
  change: number;
  changePercent: number;
  average: number;
}

export interface ReportData {
  type: ReportType;
  dateRange: DateRange;
  dataPoints: ReportDataPoint[];
  summary: ReportSummary;
  breakdown?: {
    label: string;
    value: number;
    percent: number;
  }[];
}

const toDate = (val: any): Date | null => {
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  return val ? new Date(val) : null;
};

const formatDate = (d: Date): string => d.toISOString().split('T')[0];

const buildReport = (
  type: ReportType,
  dateRange: DateRange,
  docs: any[],
  getDate: (doc: any) => Date | null,
  getValue: (doc: any) => number,
  label: string,
  getBreakdown?: (docs: any[], total: number) => { label: string; value: number; percent: number }[]
): ReportData => {
  const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) || 1;

  // Filter docs within date range
  const rangeFilteredDocs = docs.filter((d) => {
    const docDate = getDate(d);
    return docDate && docDate >= dateRange.start && docDate <= dateRange.end;
  });

  // Group by date
  const dateMap = new Map<string, number>();
  const currentDate = new Date(dateRange.start);
  while (currentDate <= dateRange.end) {
    dateMap.set(formatDate(currentDate), 0);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  rangeFilteredDocs.forEach((d) => {
    const docDate = getDate(d);
    if (docDate) {
      const key = formatDate(docDate);
      dateMap.set(key, (dateMap.get(key) || 0) + getValue(d));
    }
  });

  const dataPoints: ReportDataPoint[] = Array.from(dateMap.entries()).map(([date, value]) => ({
    date,
    value,
    label,
  }));

  const total = rangeFilteredDocs.reduce((sum, d) => sum + getValue(d), 0);

  // Previous period
  const prevStart = new Date(dateRange.start.getTime() - (dateRange.end.getTime() - dateRange.start.getTime()));
  const prevDocs = docs.filter((d) => {
    const docDate = getDate(d);
    return docDate && docDate >= prevStart && docDate < dateRange.start;
  });
  const prevTotal = prevDocs.reduce((sum, d) => sum + getValue(d), 0);
  const change = total - prevTotal;
  const changePercent = prevTotal > 0 ? (change / prevTotal) * 100 : 0;

  return {
    type,
    dateRange,
    dataPoints,
    summary: {
      total,
      change,
      changePercent,
      average: days > 0 ? Math.round(total / days) : 0,
    },
    breakdown: getBreakdown ? getBreakdown(rangeFilteredDocs, total) : undefined,
  };
};

export const useReports = (type: ReportType, dateRange: DateRange) => {
  const [report, setReport] = useState<ReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      setIsLoading(true);
      try {
        let data: ReportData;

        if (type === 'users') {
          const snapshot = await getDocs(collection(db, 'users'));
          const docs = snapshot.docs.map((d) => d.data());
          data = buildReport(
            type, dateRange, docs,
            (d) => toDate(d.createdAt),
            () => 1,
            'New Users',
            (filtered, total) => {
              const hosts = filtered.filter((d) => d.role === 'host').length;
              const admins = filtered.filter((d) => d.role === 'admin').length;
              const guests = total - hosts - admins;
              return [
                { label: 'Guests', value: guests, percent: total > 0 ? Math.round((guests / total) * 100) : 0 },
                { label: 'Hosts', value: hosts, percent: total > 0 ? Math.round((hosts / total) * 100) : 0 },
                { label: 'Admins', value: admins, percent: total > 0 ? Math.round((admins / total) * 100) : 0 },
              ];
            }
          );
        } else if (type === 'bookings') {
          const snapshot = await getDocs(collection(db, 'bookings'));
          const docs = snapshot.docs.map((d) => d.data());
          data = buildReport(
            type, dateRange, docs,
            (d) => toDate(d.createdAt),
            () => 1,
            'Bookings',
            (filtered, total) => {
              const completed = filtered.filter((d) => d.status === 'completed').length;
              const confirmed = filtered.filter((d) => d.status === 'confirmed').length;
              const cancelled = filtered.filter((d) => d.status === 'cancelled').length;
              const pending = total - completed - confirmed - cancelled;
              return [
                { label: 'Completed', value: completed, percent: total > 0 ? Math.round((completed / total) * 100) : 0 },
                { label: 'Confirmed', value: confirmed, percent: total > 0 ? Math.round((confirmed / total) * 100) : 0 },
                { label: 'Pending', value: pending, percent: total > 0 ? Math.round((pending / total) * 100) : 0 },
                { label: 'Cancelled', value: cancelled, percent: total > 0 ? Math.round((cancelled / total) * 100) : 0 },
              ];
            }
          );
        } else if (type === 'revenue') {
          const snapshot = await getDocs(collection(db, 'earnings'));
          const docs = snapshot.docs.map((d) => d.data()).filter((d) => d.status !== 'cancelled');
          data = buildReport(
            type, dateRange, docs,
            (d) => toDate(d.createdAt),
            (d) => (d.platformCommission || 0),
            'Revenue ($)',
            (filtered, total) => {
              const commissions = filtered.reduce((sum: number, d: any) => sum + (d.platformCommission || 0), 0);
              return [
                { label: 'Platform Commission (10%)', value: commissions, percent: total > 0 ? Math.round((commissions / total) * 100) : 0 },
              ];
            }
          );
        } else {
          const snapshot = await getDocs(collection(db, 'payouts'));
          const docs = snapshot.docs.map((d) => d.data()).filter((d) => d.status === 'completed');
          data = buildReport(
            type, dateRange, docs,
            (d) => toDate(d.completedAt),
            (d) => d.amount || 0,
            'Payouts ($)',
            (filtered, total) => {
              const whish = filtered.filter((d) => d.payoutMethodType === 'whish_money');
              const wire = filtered.filter((d) => d.payoutMethodType === 'wire_transfer');
              const whishTotal = whish.reduce((sum: number, d: any) => sum + (d.amount || 0), 0);
              const wireTotal = wire.reduce((sum: number, d: any) => sum + (d.amount || 0), 0);
              return [
                { label: 'Whish Money', value: whishTotal, percent: total > 0 ? Math.round((whishTotal / total) * 100) : 0 },
                { label: 'Wire Transfer', value: wireTotal, percent: total > 0 ? Math.round((wireTotal / total) * 100) : 0 },
              ];
            }
          );
        }

        if (!cancelled) {
          setReport(data);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch report:', err);
        if (!cancelled) {
          setReport(null);
          setIsLoading(false);
        }
      }
    };

    fetchReport();
    return () => { cancelled = true; };
  }, [type, dateRange.start.getTime(), dateRange.end.getTime()]);

  return {
    report,
    isLoading,
  };
};

export const convertToCSV = (report: ReportData): string => {
  if (!report) return '';
  const headers = ['Date', report.dataPoints[0]?.label || 'Value'];
  const rows = report.dataPoints.map((dp) => [dp.date, dp.value.toString()]);
  return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
};

export const downloadCSV = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

export interface SystemHealth {
  services: {
    name: string;
    status: 'operational' | 'degraded' | 'down';
    latency?: number;
  }[];
  storage: {
    used: number;
    total: number;
    percent: number;
  };
  documents: {
    collection: string;
    count: number;
  }[];
  recentErrors: {
    id: string;
    message: string;
    count: number;
    lastOccurred: Date;
  }[];
}

export const useSystemHealth = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchHealth = async () => {
      setIsLoading(true);
      try {
        // Count documents in key collections
        const collections = ['users', 'listings', 'bookings', 'reviews', 'earnings', 'payouts', 'messageThreads'];
        const counts = await Promise.all(
          collections.map(async (col) => {
            try {
              const snapshot = await getDocs(collection(db, col));
              return { collection: col, count: snapshot.size };
            } catch {
              return { collection: col, count: 0 };
            }
          })
        );

        if (!cancelled) {
          setHealth({
            services: [
              { name: 'Firebase Auth', status: 'operational' },
              { name: 'Firestore', status: 'operational' },
              { name: 'Cloud Storage', status: 'operational' },
              { name: 'Cloud Functions', status: 'operational' },
              { name: 'Firebase Hosting', status: 'operational' },
            ],
            storage: { used: 0, total: 10, percent: 0 },
            documents: counts,
            recentErrors: [],
          });
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch system health:', err);
        if (!cancelled) {
          setHealth(null);
          setIsLoading(false);
        }
      }
    };

    fetchHealth();
    return () => { cancelled = true; };
  }, []);

  return {
    health,
    isLoading,
  };
};

export default useReports;
